import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getWorkspaces } from '../services/api';
import { getStoredWorkspaceId, storeWorkspaceId } from '../utils/storage';
import { isAuthenticated } from '../services/auth';

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getWorkspaces();
      
      if (result.success && result.data) {
        const workspaceList = result.data.workspaces || result.data;
        
        if (!workspaceList || workspaceList.length === 0) {
          setWorkspaces([]);
          setCurrentWorkspace(null);
          setLoading(false);
          return;
        }
        setWorkspaces(workspaceList);
        const storedWorkspaceId = getStoredWorkspaceId();
        let workspace = null;
        
        if (storedWorkspaceId) {
          workspace = workspaceList.find(w => w.id === storedWorkspaceId || w._id === storedWorkspaceId);
        }
        if (!workspace && workspaceList.length > 0) {
          workspace = workspaceList[0];
        }
        
        if (workspace) {
          setCurrentWorkspace(workspace);
          storeWorkspaceId(workspace.id || workspace._id);
        }
      } else {
        setError(result.error || 'Failed to load workspaces');
      }
    } catch (err) {
      setError('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  const switchWorkspace = (workspace) => {
    setCurrentWorkspace(workspace);
    const workspaceId = workspace.id || workspace._id;
    storeWorkspaceId(workspaceId);
  };

  const addWorkspace = (workspace) => {
    setWorkspaces(prev => [...prev, workspace]);
    switchWorkspace(workspace);
  };

  const updateWorkspaceInState = (workspaceId, updates) => {
    setWorkspaces(prev => 
      prev.map(w => (w.id || w._id) === workspaceId ? { ...w, ...updates } : w)
    );
    
    const currentId = currentWorkspace?.id || currentWorkspace?._id;
    if (currentId === workspaceId) {
      setCurrentWorkspace(prev => ({ ...prev, ...updates }));
    }
  };

  const removeWorkspace = (workspaceId) => {
    setWorkspaces(prev => prev.filter(w => (w.id || w._id) !== workspaceId));
    
    const currentId = currentWorkspace?.id || currentWorkspace?._id;
    if (currentId === workspaceId) {
      const remaining = workspaces.filter(w => (w.id || w._id) !== workspaceId);
      if (remaining.length > 0) {
        switchWorkspace(remaining[0]);
      } else {
        setCurrentWorkspace(null);
      }
    }
  };

  useEffect(() => {
    const initWorkspaces = async () => {
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (isAuthenticated()) {
        loadWorkspaces();
      } else {
        setLoading(false);
        setWorkspaces([]);
        setCurrentWorkspace(null);
      }
    };
    
    initWorkspaces();
  }, []);

  // Helper to get available providers based on workspace API keys
  const getAvailableProviders = () => {
    if (!currentWorkspace) return null;
    return {
      openai: currentWorkspace.is_openai_available || false,
      anthropic: currentWorkspace.is_anthropic_available || false,
      groq: currentWorkspace.is_groq_available || false,
    };
  };

  const value = {
    workspaces,
    currentWorkspace,
    loading,
    error,
    switchWorkspace,
    loadWorkspaces,
    addWorkspace,
    updateWorkspaceInState,
    removeWorkspace,
    getAvailableProviders,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

WorkspaceProvider.propTypes = {
  children: PropTypes.node.isRequired
};

